import {
  CalendarCheck,
  CalendarOff,
  BookOpen,
  Gift,
  Coins,
  FileQuestion,
  BadgeDollarSign,
  Users,
  MessageSquare,
  Building2,
  MapPin,
  SlidersHorizontal,
  BarChart3,
  LayoutDashboard,
  ReceiptText,
  type LucideIcon,
} from 'lucide-react';
import { USER_TYPE } from '@/features/users/constants/user.constants';

export interface NavVisibilityContext {
  userTypeId: number | null;
  departmentName: string | null;
}

interface NavChild {
  label: string;
  href: string;
  activeWhen?: string[];
  upcoming?: boolean;
  isHidden?: boolean;
  action?: string;
  visibleWhen?: (ctx: NavVisibilityContext) => boolean;
}

interface NavGroup {
  label: string;
  icon: LucideIcon;
  isHidden?: boolean;
  children: NavChild[];
}

export const ATTENDANCE_NAV: NavGroup[] = [
  {
    label: 'Job Card Unit',
    icon: CalendarCheck,
    isHidden: true,
    children: [
      { label: 'Daily Dashboard', href: '/attendance' },
      {
        label: 'Job Card',
        href: '/attendance/job-card',
        activeWhen: ['/attendance/employee-report'],
      },
      {
        label: 'Job Card (Night Shift)',
        href: '/attendance/shift-job-card',
        activeWhen: ['/attendance/shift-employee-report'],
      },
    ],
  },
];

export const HR_FORM_NAV: NavGroup[] = [
  {
    label: 'HR Form',
    icon: CalendarOff,
    children: [
      { label: 'Leave Applications', href: '/hr-form/leave-applications' },
      {
        label: 'Leave Report',
        href: '/hr-form/leave-report',
        // Only SA/ADM, or a General User in an MIS department.
        visibleWhen: (ctx) =>
          ctx.userTypeId === USER_TYPE.SA ||
          ctx.userTypeId === USER_TYPE.ADM ||
          (ctx.userTypeId === USER_TYPE.GENERAL && /\bMIS\b/i.test(ctx.departmentName ?? '')),
      },
      { label: 'Leave Types', href: '/settings/leave-types' },
    ],
  },
];

export const GEO_LOCATION_NAV: NavGroup[] = [
  {
    label: 'Geo Location',
    icon: MapPin,
    children: [{ label: 'MO Travel History', href: '/geo-location/mo-travel-history' }],
  },
];

export const USER_MANAGEMENT_NAV: NavGroup[] = [
  {
    label: 'User Management',
    icon: Users,
    isHidden: true,
    children: [
      { label: 'All Users', href: '/users' },
      { label: 'Roles', href: '/users/roles' },
      { label: 'Admin Users', href: '/users/admin', activeWhen: ['/users/create/admin'] },
      { label: 'Agent Users', href: '/users/agent', activeWhen: ['/users/create/agent'] },
      { label: 'General Users', href: '/users/general', activeWhen: ['/users/create/general'] },
      {
        label: 'Marketing Users',
        href: '/users/marketing',
        activeWhen: ['/users/create/marketing'],
      },
      { label: 'Special Users', href: '/users/special', activeWhen: ['/users/create/special'] },
    ],
  },
];

export const INSTITUTE_NAV: NavGroup[] = [
  {
    label: 'Institute Unit',
    icon: Building2,
    children: [
      { label: 'Institute Types', href: '/settings/institute-types' },
      { label: 'View Institutes', href: '/settings/institutes' },
      { label: 'Institute Mapping', href: '/settings/institutes/user-mapping' },
      { label: 'Institute Submissions', href: '/settings/institute-submissions' },
      { label: 'Teacher Submissions', href: '/settings/teacher-submissions' },
    ],
  },
];

export const FEEDBACK_NAV: NavGroup[] = [
  {
    label: 'Feedback Unit',
    icon: MessageSquare,
    isHidden: true,
    children: [
      { label: 'View Feedbacks', href: '/feedback/list' },
      { label: 'Feedback Types', href: '/settings/feedback-types' },
      { label: 'Surveys', href: '/feedback/surveys' },
    ],
  },
];

export const ADMIN_UNIT_NAV: NavGroup[] = [
  {
    label: 'Admin Unit',
    icon: SlidersHorizontal,
    isHidden: true,
    children: [
      { label: 'Limit Types', href: '/settings/limit-types' },
      { label: 'Web URLs', href: '/settings/web-urls' },
    ],
  },
];

export const DASHBOARD_UNIT_NAV: NavGroup[] = [
  {
    label: 'Dashboard Unit',
    icon: LayoutDashboard,
    isHidden: true,
    children: [
      { label: 'System Dashboard', href: '/dashboards/system' },
      { label: 'DCR Dashboard', href: '/dashboards/dcr' },
      { label: 'DER Dashboard', href: '/dashboards/der' },
      { label: 'Zonal Statement', href: '/dashboards/zonal-statement' },
      { label: 'DCR & DER Pending', href: '/dashboards/pending' },
    ],
  },
];

export const BILLING_UNIT_NAV: NavGroup[] = [
  {
    label: 'Billing Unit',
    icon: ReceiptText,
    isHidden: true,
    children: [
      { label: 'Offline DCR Verify', href: '/billing/offline-dcr-verify' },
      { label: 'Offline DER Verify', href: '/billing/offline-der-verify' },
      { label: 'Expense Approve', href: '/billing/expense-approve' },
      { label: 'DCR Approve', href: '/billing/dcr-approve' },
      { label: 'DCR & DER Approval', href: '/billing/approval' },
      { label: 'View All Cost', href: '/billing/all-cost' },
      { label: 'DCR Cost Amendment', href: '/billing/cost-amendment' },
      { label: 'Web Expense Info', href: '/billing/web-expense' },
    ],
  },
];

export const PRODUCT_UNIT_NAV: NavGroup[] = [
  {
    label: 'Product Unit',
    icon: BookOpen,
    isHidden: true,
    children: [
      { label: 'Specimen Adjustment', href: '/product/specimen-adjustment' },
      { label: 'View Specimen Institution', href: '/product/specimen-institution' },
      { label: 'View DCR Date to Date', href: '/product/dcr-date-to-date' },
      { label: 'View DCR Sheet', href: '/product/dcr-sheet' },
      { label: 'Specimen List View', href: '/product/specimen-list-view' },
      { label: 'Specimen List', href: '/product/specimens' },
      { label: 'Specimen Distribution', href: '/product/specimen-distribution' },
      { label: 'Book Type', href: '/product/book-types' },
      { label: 'Book Edition', href: '/product/book-editions' },
    ],
  },
];

export const PROMOTION_UNIT_NAV: NavGroup[] = [
  {
    label: 'Promotion Unit',
    icon: Gift,
    isHidden: true,
    children: [
      { label: 'Promotional Item Adjustment', href: '/promotion/adjustment' },
      { label: 'Promotional Item Distribution', href: '/promotion/distribution' },
      { label: 'Promotional Item List', href: '/promotion/items' },
      { label: 'Promotional Item Type', href: '/promotion/item-types' },
    ],
  },
];

export const MARKET_PROMOTION_NAV: NavGroup[] = [
  {
    label: 'Market Promotion Unit',
    icon: Coins,
    isHidden: true,
    children: [
      { label: 'Distribution Summary', href: '/market-promotion/distribution-summary' },
      { label: 'Region-Wise Market Promotion', href: '/market-promotion/region-summary' },
      { label: 'Market Promotion Adjustment', href: '/market-promotion/adjustment' },
      { label: 'Market Promotion Approval', href: '/market-promotion/approvals' },
      { label: 'Market Promotion MIS Approval', href: '/market-promotion/mis-approvals' },
      { label: 'Market Promotion Approval Report', href: '/market-promotion/approval-report' },
      { label: 'Market Promotion Project', href: '/market-promotion/projects' },
      { label: 'Market Promotion Budget Entry', href: '/market-promotion/budget-entry' },
      { label: 'Market Promotion Publish Panel', href: '/market-promotion/publish-panel' },
      { label: 'Market Promotion Accounts Adjust', href: '/market-promotion/accounts-adjust' },
      { label: 'Donation Requisition', href: '/market-promotion/requisitions' },
      { label: 'Cash Collection', href: '/market-promotion/cash-collection' },
    ],
  },
];

export const QUESTION_UNIT_NAV: NavGroup[] = [
  {
    label: 'Question Unit',
    icon: FileQuestion,
    isHidden: true,
    children: [
      {
        label: 'Question Download by Class',
        href: '/question/download-by-class',
        action: 'BoardClassQuesdownload/MonitorSelect',
      },
      {
        label: 'Question Download by Board',
        href: '/question/download-by-board',
        action: 'BoardQuestionMonitor/Index',
      },
      {
        label: 'Question Download',
        href: '/question/download',
        action: 'QuestionMonitor/Index',
      },
      {
        label: 'Question Upload',
        href: '/question/upload',
        action: 'QuestionProject/UploadView',
      },
      {
        label: 'Project Management',
        href: '/question/projects',
        action: 'QuestionProject/Index',
      },
      {
        label: 'Subject Management',
        href: '/question/subjects',
        action: 'SetClass/Index',
      },
    ],
  },
];

export const PUBLISH_UNIT_NAV: NavGroup[] = [
  {
    label: 'Publish Unit',
    icon: BadgeDollarSign,
    isHidden: true,
    children: [
      { label: 'Sales Publish Panel', href: '/publish/sales' },
      { label: 'MIS Publish Panel', href: '/publish/mis' },
      { label: 'Accounts Disburse Panel', href: '/publish/accounts' },
    ],
  },
];

export const REPORTS_NAV: NavGroup[] = [
  {
    label: 'Reports',
    icon: BarChart3,
    isHidden: true,
    children: [{ label: 'App Version Check', href: '/reports/app-version-check' }],
  },
];

export interface NavSection {
  id: string;
  label?: string;
  groups: NavGroup[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'units',
    groups: [
      ...DASHBOARD_UNIT_NAV,
      ...BILLING_UNIT_NAV,
      ...PRODUCT_UNIT_NAV,
      ...PROMOTION_UNIT_NAV,
      ...MARKET_PROMOTION_NAV,
      ...QUESTION_UNIT_NAV,
      ...PUBLISH_UNIT_NAV,
      ...ADMIN_UNIT_NAV,
      ...REPORTS_NAV,
      ...USER_MANAGEMENT_NAV,
      ...INSTITUTE_NAV,
      ...ATTENDANCE_NAV,
      ...HR_FORM_NAV,
      ...GEO_LOCATION_NAV,
      ...FEEDBACK_NAV,
    ],
  },
];

export type { NavGroup, NavChild };
